import { Component } from '@theme/component'

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent']

  /** @type {AbortController | null} */
  #triggerController = null

  /**
   * @param {HTMLElement} content
   * @param {boolean} isCollapsed
   */
  #setContentInteractivity(content, isCollapsed) {
    if ('inert' in content) {
      content.inert = isCollapsed
      return
    }

    content.setAttribute('aria-hidden', String(isCollapsed))
    content.style.pointerEvents = isCollapsed ? 'none' : ''

    const focusableElements = content.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]'
    )

    for (const element of focusableElements) {
      if (!(element instanceof HTMLElement)) continue

      if (isCollapsed) {
        const tabindex = element.getAttribute('tabindex')
        if (tabindex != null) {
          element.dataset.disclosureTabindex = tabindex
        } else {
          delete element.dataset.disclosureTabindex
        }

        element.setAttribute('tabindex', '-1')
      } else if ('disclosureTabindex' in element.dataset) {
        const { disclosureTabindex } = element.dataset
        if (disclosureTabindex) {
          element.setAttribute('tabindex', disclosureTabindex)
        } else {
          element.removeAttribute('tabindex')
        }
        delete element.dataset.disclosureTabindex
      } else {
        element.removeAttribute('tabindex')
      }
    }
  }

  #syncDisclosureState() {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs
    const isExpanded = trigger.matches('[aria-expanded="true"]')
    this.#setContentInteractivity(content, !isExpanded)
  }

  connectedCallback() {
    super.connectedCallback()
    this.#bindTrigger()
    this.#syncDisclosureState()
  }

  /**
   * Re-syncs interactivity (inert / tabindex) when the component is morphed
   * by the Section Rendering API. Without this, a section update can leave
   * the panel content focusable while the trigger reads as collapsed (or
   * vice versa) because the underlying aria-expanded was rewritten by morph
   * without the JS-managed mirror state being re-applied.
   */
  updatedCallback() {
    super.updatedCallback?.()
    this.#bindTrigger()
    this.#syncDisclosureState()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#triggerController?.abort()
    this.#triggerController = null
  }

  /**
   * Binds the toggle directly to the trigger button instead of relying solely
   * on the global `on:click` delegation in `component.js`. The delegated path
   * silently no-ops if this element has not upgraded yet (its tag ends in
   * `-custom`, not `-component`), which left the panel unresponsive in WebKit
   * when the module loaded after the first interaction. A native button fires
   * `click` on Enter/Space, so a single click listener also covers keyboard.
   */
  #bindTrigger() {
    const { disclosureTrigger: trigger } = this.refs
    if (!(trigger instanceof HTMLElement)) return

    this.#triggerController?.abort()
    this.#triggerController = new AbortController()

    trigger.addEventListener('click', this.toggleDisclosure, {
      signal: this.#triggerController.signal
    })
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs
    const expanded = trigger.matches('[aria-expanded="true"]')
    trigger.setAttribute('aria-expanded', String(!expanded))
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    )
    this.#setContentInteractivity(content, expanded)
  }
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom)
}
